import axios from "../../../../api/axios";
import { formatDate } from "./helpers";

/**
 * API endpoints for tenant information operations
 */
export const ENDPOINTS = {
  MASTERSHEET: "v2/master_sheet",
  EXPORT_MASTERSHEET_EXCEL: "v2/master_sheet/:mastersheetId/export-excel",
  ADD_TENANT_DETAILS: "v2/master_sheet/items",
  EDIT_TENANT_DETAILS: "v2/master_sheet/items",
  GET_ITEM_HISTORY: "v2/master_sheet/items/:_id/history",
  UPDATE_COMMENT: "v2/master_sheet/items/:_id/comment",
  UPDATE_STATUS: "v2/master_sheet/items/:_id/status",
  UPLOAD_MASTERSHEET_ITEMS: "v2/master_sheet/layout/upload",
  DOWNLOAD_MASTERSHEET_ITEMS: "v2/master_sheet/layout/download",
  PROPERTY_LAYOUT_DETAILS: "v2/master_sheet/layout/",
  PROPERTY_LIST: "v2/master_sheet/property-list",
  PROPERTY_FILTERS: "v2/master_sheet/get-property-based-filters-mastersheet",
  GET_LEASES_BY_PROPERTY: "v2/lease/property/:propertyId",
  GET_LEASE_BY_ID: "v2/lease/get-lease-by-id/:id",
  TERMINATE_LEASES: "v2/lease/terminate-leases",
  GET_MOVE_IN_OUT_REPORTS_BY_RESIDENT: "v2/move-in-out-inspections/by-resident",

  GET_DOCUMENTS_BY_PROPERTY_ID: "v2/master_sheet/documents",
  UPLOAD_DOCUMENT: "v2/master_sheet/documents/upload",
  ADD_DOCUMENT_TO_MASTERSHEET: "v2/master_sheet/documents/add",

  GET_DOCUMENT_BY_VERSION_NO_FOR_PREVIEW: "v2/master_sheet/documents/version",

  GET_ARCHIVE_MASTERSHEET: "v2/master_sheet/archive",
  ARCHIVE_TENANT_LEASE: "v2/master_sheet/archive",
  GET_ARCHIVED_ITEM_HISTORY: "v2/master_sheet/archive/:archivedItemId/memos",
  GENERATE_SIGNED_DOCUMENT_URL: "v2/lease/:leaseId/generate-signed-document-url",
  EXPORT_LEASES_ZIP: "v2/master_sheet/export-leases-zip",
  GET_DEPOSIT_VS_RENT_REPORT: "v2/master_sheet/:mastersheetId/deposit-vs-rent",
  EXPORT_DEPOSIT_VS_RENT_EXCEL: "v2/master_sheet/:mastersheetId/deposit-vs-rent/export-excel",
  GET_VACANCY_LEASE_EXPIRY_REPORT: "v2/master_sheet/:mastersheetId/vacancy-lease-expiry",
  EXPORT_VACANCY_LEASE_EXPIRY_EXCEL: "v2/master_sheet/:mastersheetId/vacancy-lease-expiry/export-excel",
  GET_UTILITY_ACCOUNTS_REPORT: "v2/master_sheet/:mastersheetId/utility-accounts",
  EXPORT_UTILITY_ACCOUNTS_EXCEL: "v2/master_sheet/:mastersheetId/utility-accounts/export-excel",
  GET_INSURANCE_REPORT: "v2/master_sheet/:mastersheetId/insurance-report",
  EXPORT_INSURANCE_REPORT_EXCEL: "v2/master_sheet/:mastersheetId/insurance-report/export-excel",
  GET_PAD_REPORT: "v2/master_sheet/:mastersheetId/pad-report",
  EXPORT_PAD_REPORT_EXCEL: "v2/master_sheet/:mastersheetId/pad-report/export-excel",
  GET_HISTORIC_AS_OF: "v2/master_sheet/historic/:mastersheetId/as-of",
  EXPORT_HISTORIC_AS_OF_EXCEL: "v2/master_sheet/historic/:mastersheetId/as-of/export-excel",
  GET_HISTORIC_RESIDENTS: "v2/master_sheet/historic/:mastersheetId/residents",
  EXPORT_HISTORIC_RESIDENTS_EXCEL: "v2/master_sheet/historic/:mastersheetId/residents/export-excel",
  RESIDENT_DIRECTORY: "v2/master_sheet/residents-directory",
  EXPORT_RESIDENT_DIRECTORY_EXCEL: "v2/master_sheet/residents-directory/export-excel",
  RESIDENT_DIRECTORY_CONTACT: "v2/master_sheet/residents-directory/contact",
  RESIDENT_DIRECTORY_TEMPLATE: "v2/master_sheet/residents-directory/template",
  RESIDENT_DIRECTORY_IMPORT: "v2/master_sheet/residents-directory/import",
  RESIDENT_DIRECTORY_UNIT: "v2/master_sheet/residents-directory/unit",
  GET_HISTORIC_AVAILABLE_MONTHS: "v2/master_sheet/historic/:mastersheetId/available-months",
  CREATE_HISTORIC_SNAPSHOT: "v2/master_sheet/historic/:mastersheetId/snapshot",
};

/**
 * Maps API response to normalized tenant info object
 * @param {Object} data - The API response data
 * @param {Object} residence - Optional residence information
 * @returns {Object} Normalized tenant info object
 */
const mapResponseToTenantInfo = (data, residence = {}) => {
  const {
    tenants = [],
    occupants = [],
    utility_accounts = "",
    no_of_bedrooms = "",
    area_in_sqft = "",
    suite_no = "",
    security_deposit = "",
    target_rent = "",
    basic_rent = "",
    pets = "",
    lease_start = "",
    lease_end = "",
    comments = "",
    documents = [],
  } = data || {};

  return {
    ...(Object.keys(residence).length && { residence }),
    tenants,
    occupants,
    utility_accounts,
    no_of_bedrooms,
    area_in_sqft,
    suite_no,
    security_deposit,
    target_rent,
    basic_rent,
    pets,
    lease_start: formatDate(lease_start),
    lease_end: formatDate(lease_end),
    comments,
    documents,
  };
};

/**
 * Fetches tenant information by property ID, building name, and suite number
 * @param {string} property_id - Property ID
 * @param {string} building_name - Building name
 * @param {string} primary_id - Primary ID
 * @param {string} suite_no - Suite number
 * @returns {Promise<Object|null>} Tenant information or null if error
 */
export const getTenantInfo = async (property_id, building_name, primary_id, suite_no) => {
  try {
    const { data } = await axios.get(
      `${ENDPOINTS.LEASE_INFO}/${primary_id}/${building_name}/${suite_no}`
    );

    if (data?.status !== 200) return null;

    return mapResponseToTenantInfo(data.data, {
      property_id,
      building_name,
      primary_id,
    });
  } catch (error) {
    console.error("Error fetching tenant info:", error);
    return null;
  }
};

/**
 * Fetches property list based on user role and ID
 * @param {Object} userData - User data object containing role and ID
 * @returns {Promise<Object>} Filtered property data
 */
export const getPropertyList = async (userData) => {
  if (!userData?.id) return null;

  try {
    const { data } = await axios.get(
      `v2/master_sheet/property-list?role=${userData.role || ""}&id=${userData.id || ""}`
    );

    const properties = data.data || [];

    return {
      propertyList: properties,
      building_list: properties.length ? properties[0].building || [] : [],
    };
  } catch (error) {
    console.error("Failed to fetch property list:", error);
    return null;
  }
};

/**
 * Fetches property list based on user role and ID
 * @param {Object} userData - User data object containing role and ID
 * @returns {Promise<Object>} Filtered property data
 */
export const getPropertyFilters = async ({ property_id, building_name }) => {
  const { data } = await axios.get(ENDPOINTS.PROPERTY_FILTERS, {
    params: { property_id, building_name },
  });

  return data;
};

export const getMasterSheetLayout = async (property_id, building_name = "") => {
  try {
    const endpoint = building_name
      ? `${ENDPOINTS.PROPERTY_LAYOUT_DETAILS}${property_id}/${encodeURIComponent(building_name)}`
      : `${ENDPOINTS.PROPERTY_LAYOUT_DETAILS}${property_id}`;

    const { data } = await axios.get(endpoint);

    if (data?.success) {
      return data.data;
    }

    return null;
  } catch (error) {
    console.error("Error fetching MasterSheet layout:", error);
    return null;
  }
};

// ---- All Properties (portfolio) ----------------------------------------------
// Scope is resolved server-side from the JWT — the client sends no ids.
export const getPortfolio = async () => {
  const { data } = await axios.get("v2/master_sheet/portfolio");
  return data;
};

export const getPortfolioReport = async () => {
  const { data } = await axios.get("v2/master_sheet/portfolio/report");
  return data;
};

export const exportPortfolioExcel = async () => {
  const response = await axios.get("v2/master_sheet/portfolio/export-excel", { responseType: "blob" });
  return response.data;
};

export const getPadDashboard = async (month) => {
  const { data } = await axios.get("v2/pad-info/dashboard", { params: month ? { month } : {} });
  return data;
};

export const getMastersheet = async ({
  property_id,
  building_name,
  filters = {}
}) => {
  const { data } = await axios.get(ENDPOINTS.MASTERSHEET, {
    params: {
      property_id,
      building_name,
      filters
    },
  });

  return data;
};

/**
 * Exports MasterSheet data to an Excel file by MasterSheet ID
 * @param {string} mastersheetId - The ID of the MasterSheet to export
 * @param {Object} [filters={}] - Optional filters to apply before export
 * @returns {Promise<void>} Downloads the Excel file
 */
export const exportMastersheetToExcel = async (mastersheetId, filters = {}) => {
  if (!mastersheetId) {
    throw new Error("mastersheetId is required to export Excel.");
  }

  try {
    const endpoint = ENDPOINTS.EXPORT_MASTERSHEET_EXCEL.replace(":mastersheetId", mastersheetId);
    const params = new URLSearchParams();

    if (Object.keys(filters).length > 0) {
      params.append("filters", JSON.stringify(filters));
    }

    const response = await axios.get(`${endpoint}?${params.toString()}`, {
      responseType: "blob", // Important for file download
    });

    // Create a Blob URL and trigger the download
    const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `MasterSheet_${mastersheetId}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error exporting MasterSheet to Excel:", error);
    throw error;
  }
};

/**
 * Adds new tenant information
 * @param {Object} params
 * @param {Object} params.values - Tenant information to add
 * @param {string} params.property_id - Property ID
 * @param {string} [params.building_name] - Optional building name
 * @returns {Promise<Object>} Added tenant information response
 */
export const addTenant = async ({ values, property_id, building_name }) => {
  try {
    let url = `${ENDPOINTS.ADD_TENANT_DETAILS}/${property_id}/items`;

    if (building_name) {
      url += `?building_name=${encodeURIComponent(building_name)}`;
    }

    const response = await axios.post(url, values);
    return response;
  } catch (error) {
    console.error('Error adding tenant:', error);
    throw error;
  }
};

/**
 * Updates existing tenant information
 * @param {Object} params
 * @param {Object} params.values - Updated tenant information
 * @param {string} params.property_id - Property ID
 * @param {string} params._id - Tenant item ID
 * @param {string} [params.building_name] - Optional building name
 * @returns {Promise<Object>} Updated tenant information response
 */
export const updateTenant = async ({ values, property_id, building_name, _id }) => {
  try {
    let url = `${ENDPOINTS.EDIT_TENANT_DETAILS}/${property_id}/items/${_id}`;

    if (building_name) {
      url += `?building_name=${encodeURIComponent(building_name)}`;
    }

    const response = await axios.put(url, values);
    return response;
  } catch (error) {
    console.error('Error updating tenant:', error);
    throw error;
  }
};

/**
 * Get all leases by property ID, building name, and unit number
 * @param {string} propertyId - The property ID
 * @param {string} buildingName - The building name
 * @param {string} unitNo - The unit number
 * @param {string} token - JWT token for authorization
 * @returns {Promise<Object>} List of leases
 */
export const getLeasesByProperty = async (propertyId, buildingName, unitNo, isArchived = false) => {
  if (!propertyId || !buildingName || !unitNo) {
    throw new Error('propertyId, buildingName, and unitNo are required');
  }

  const url = ENDPOINTS.GET_LEASES_BY_PROPERTY.replace(':propertyId', propertyId);
  const queryParams = new URLSearchParams({
    building_name: buildingName,
    unit_no: unitNo,
    isArchived: isArchived.toString(),
  });

  const response = await axios.get(`${url}?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get lease details by ID
 * @param {string} leaseId
 * @returns {Promise<Object>} Lease object
 */
export const getLeaseByIdForCompletePreview = async (leaseId) => {
  const url = ENDPOINTS.GET_LEASE_BY_ID.replace(':id', leaseId);
  const response = await axios.get(`${url}`);
  return response.data;
};

/**
 * A resident's own Move-In / Move-Out reports (matched on property + unit + name).
 * @param {string} propertyId
 * @param {string|number} unitNo
 * @param {string[]} names - lease holders, occupants and the legacy tenants string
 */
export const getMoveInOutReportsByResident = async (propertyId, unitNo, names = []) => {
  const queryParams = new URLSearchParams({ propertyId, unitNo: String(unitNo) });
  names.forEach((name) => queryParams.append('names', name));
  const response = await axios.get(`${ENDPOINTS.GET_MOVE_IN_OUT_REPORTS_BY_RESIDENT}?${queryParams.toString()}`);
  return response.data;
};

export const requestLeasesZipExport = async (mastersheetId) => {
  const response = await axios.post(ENDPOINTS.EXPORT_LEASES_ZIP, {
    mastersheet_id: mastersheetId,
  });
  return response.data;
};

/**
 * Generates a signed URL for a document with embedded signatures
 * @param {string} leaseId - The lease ID
 * @param {string} documentId - The document ID
 * @param {Array} signatures - Array of signature objects
 * @returns {Promise<Object>} Signed URL data
 */
export const generateSignedDocumentUrl = async (leaseId, documentId, signatures) => {
  if (!leaseId || !documentId || !signatures || !Array.isArray(signatures)) {
    throw new Error('leaseId, documentId, and signatures array are required');
  }

  try {
    const url = ENDPOINTS.GENERATE_SIGNED_DOCUMENT_URL.replace(':leaseId', leaseId);
    const response = await axios.post(url, { document_id: documentId, signatures });
    return response.data;
  } catch (error) {
    console.error('Error generating signed document URL:', error);
    throw error;
  }
};

/**
 * Get documents by property ID, building name, and unit number
 * @param {string} propertyId - The property ID
 * @param {string} buildingName - The building name
 * @param {string} unitNo - The unit number
 * @returns {Promise<Object>} List of documents
 */
export const getDocumentsBy = async (propertyId, buildingName, unitNo) => {
  if (!propertyId || !buildingName || !unitNo) {
    throw new Error('propertyId, buildingName, and unitNo are required');
  }

  const url = ENDPOINTS.GET_DOCUMENTS_BY_PROPERTY_ID;
  const queryParams = new URLSearchParams({
    property_id: propertyId,
    building_name: buildingName,
    unit_no: unitNo,
  });
  const response = await axios.get(`${url}?${queryParams.toString()}`);
  return response.data;
};

/**
 * Placeholder for uploading documents to S3 (appears to be a duplicate - needs clarification)
 * @param {string} propertyId - The property ID
 * @param {string} buildingName - The building name
 * @param {string} unitNo - The unit number
 * @returns {Promise<Object>} Document data
 */
export const uploadDocumentToS3 = async (propertyId, buildingName, unitNo) => {
  if (!propertyId || !buildingName || !unitNo) {
    throw new Error('propertyId, buildingName, and unitNo are required');
  }

  const url = ENDPOINTS.GET_DOCUMENTS_BY_PROPERTY_ID;
  const queryParams = new URLSearchParams({
    property_id: propertyId,
    building_name: buildingName,
    unit_no: unitNo,
  });
  const response = await axios.get(`${url}?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get documents by unit number
 * @param {string} propertyId - The property ID
 * @param {string} buildingName - The building name
 * @param {string} unitNo - The unit number
 * @param {boolean} isActive - Filter for active documents
 * @returns {Promise<Object>} List of documents
 */
export const getDocumentsByUnitNo = async (propertyId, buildingName, unitNo, isActive = true) => {
  const queryParams = new URLSearchParams({
    property_id: propertyId,
    building_name: buildingName,
    unit_no: unitNo,
    isActive: isActive.toString(),
  });
  const response = await axios.get(`${ENDPOINTS.GET_DOCUMENTS_BY_PROPERTY_ID}?${queryParams.toString()}`);
  return response.data;
};

/**
 * Upload requested documents
 * @param {FormData} formData - Form data containing the document
 * @param {Function} onUploadProgress - Progress callback function
 * @returns {Promise<Object>} Uploaded document data
 */
export const uploadRequestedDocuments = async (formData, onUploadProgress) => {
  const response = await axios.post(ENDPOINTS.UPLOAD_DOCUMENT, formData, {
    onUploadProgress,
  });
  return response.data;
};

/**
 * Add documents to mastersheet
 * @param {string} propertyId - The property ID
 * @param {string} buildingName - The building name
 * @param {string} unitNo - The unit number
 * @param {Object} document - Document data to add
 * @returns {Promise<Object>} Added document data
 */
export const addDocumentsToMastersheet = async (propertyId, buildingName, unitNo, document) => {
  const queryParams = new URLSearchParams({
    property_id: propertyId,
    building_name: buildingName,
    unit_no: unitNo,
  });
  const response = await axios.post(`${ENDPOINTS.ADD_DOCUMENT_TO_MASTERSHEET}?${queryParams.toString()}`, document);
  return response.data;
};

/**
 * Updates comment for a mastersheet item
 * @param {string} id - The ID of the item to update
 * @param {string} comment - The new comment
 * @returns {Promise<Object|null>} Updated item data or null if error
 */
export const updateCommentForMastersheetItem = async (id, comments) => {
  try {
    // Construct the URL by replacing :_id
    const url = ENDPOINTS.UPDATE_COMMENT.replace(':_id', id);

    // Make the PUT request
    const { data } = await axios.put(url, { comments });

    if (data && data.success) {
      return data.data; // Return the updated item from the response
    }

    return null;
  } catch (error) {
    console.error('Failed to update comment:', error);
    return null;
  }
};

/**
 * Updates status for a mastersheet item
 * @param {string} id - The ID of the item to update
 * @param {string} status - The new status
 * @returns {Promise<Object|null>} Updated item data or null if error
 */
export const updateStatusForMastersheetItem = async (id, status) => {
  try {
    // Construct the URL by replacing :_id
    const url = ENDPOINTS.UPDATE_STATUS.replace(':_id', id);

    // Make the PUT request
    const { data } = await axios.put(url, { status });

    if (data && data.success) {
      return data.data; // Return the updated item from the response
    }

    return null;
  } catch (error) {
    console.error('Failed to update status:', error);
    return null;
  }
};

/**
 * Gets history for a mastersheet item
 * @param {string} id - The ID of the item to get history for
 * @param {Object} options - Optional query parameters
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 50)
 * @param {string} options.field_name - Filter by specific field name
 * @returns {Promise<Object|null>} History data with pagination or null if error
 */
export const getItemHistory = async (id, options = {}) => {
  try {
    if (!id) {
      throw new Error('Item ID is required');
    }

    // Construct the URL by replacing :_id
    const url = ENDPOINTS.GET_ITEM_HISTORY.replace(':_id', id);

    // Build query parameters
    const params = new URLSearchParams();

    if (options.page) {
      params.append('page', options.page.toString());
    }

    if (options.limit) {
      params.append('limit', options.limit.toString());
    }

    if (options.field_name) {
      params.append('field_name', options.field_name);
    }

    // Make the GET request
    const { data } = await axios.get(`${url}?${params.toString()}`);

    if (data && data.success) {
      return data.data; // Return the history data with pagination info
    }

    return null;
  } catch (error) {
    console.error('Failed to fetch item history:', error);
    throw error;
  }
};

/**
 * Terminates leases for a specific unit
 * @param {string} property_id - ID of the property
 * @param {string} building_name - Name of the building
 * @param {string} unit_no - Unit number
 * @param {string} status - New status (e.g., "terminated")
 * @param {string} reason - Reason for termination
 * @param {string} user_id - ID of the user performing the action
 * @returns {Promise<Object|null>} Result of the termination operation or null if failed
 */
export const terminateLeases = async (property_id, building_name, unit_no, status, reason) => {
  try {
    const payload = {
      property_id,
      building_name,
      unit_no,
      status,
      reason,
    };

    const { data } = await axios.put(ENDPOINTS.TERMINATE_LEASES, payload);

    if (data && data.success) {
      return data.data;
    }

    return null;
  } catch (error) {
    console.error('Failed to terminate lease:', error);
    throw error;
  }
};

/**
 * Gets a signed URL for a specific document version
 * @param {string} propertyId - The property ID
 * @param {string} buildingName - The building name
 * @param {string} unitNo - The unit number
 * @param {string} documentType - The document type (e.g., 'lease_agreement', 'property_deed')
 * @param {number} versionNumber - Version number (0 for current/latest version, positive integer for specific version)
 * @returns {Promise<Object>} Document version data with signed URL
 * @throws {Error} If required parameters are missing or API call fails
 */
export const getDocumentVersionForPreview = async (propertyId, buildingName, unitNo, documentType, versionNumber) => {
  if (!propertyId || !buildingName || !unitNo || !documentType) {
    throw new Error('propertyId, buildingName, unitNo, and documentType are required');
  }

  try {
    const queryParams = new URLSearchParams({
      property_id: propertyId,
      building_name: buildingName,
      unit_no: unitNo,
      document_type: documentType,
      version_number: versionNumber.toString(),
    });

    const response = await axios.get(`${ENDPOINTS.GET_DOCUMENT_BY_VERSION_NO_FOR_PREVIEW}?${queryParams.toString()}`);

    if (response.data && response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data?.message || 'Failed to fetch document version');
  } catch (error) {
    console.error('Error fetching document version:', error);
    throw error;
  }
};

// A page holds 100 archived units, so a building only actually paginates once more than
// 100 tenancies are archived — below that everything arrives in one go, like the live sheet.
export const getArchiveMastersheet = async ({ mastersheetId, page = 1, limit = 100 }) => {
  const { data } = await axios.get(`${ENDPOINTS.GET_ARCHIVE_MASTERSHEET}/${mastersheetId}`, { params: { page, limit } });
  return data;
};

export const archiveMastersheetItem = async (
  mastersheetId,
  propertyId,
  buildingName,
  unitNo,
  archivedReason
) => {
  if (!propertyId || !buildingName || !unitNo || !archivedReason) {
    throw new Error('propertyId, buildingName, unitNo, and archivedReason are required');
  }

  try {
    const payload = {
      mastersheetId,
      property_id: propertyId,
      building_name: buildingName,
      unitNo,
      archivedReason
    };

    const response = await axios.post(ENDPOINTS.ARCHIVE_TENANT_LEASE, payload);

    if (response.data?.success) {
      return response.data;
    }

    throw new Error(response.data?.message || 'Failed to archive mastersheet item');
  } catch (error) {
    console.error('Error archiving mastersheet item:', error);
    throw error;
  }
};

/**
 * Gets memos for an archived mastersheet item before document creation date
 * @param {string} archivedItemId - The ID of the archived item
 * @param {Object} options - Optional query parameters
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 50)
 * @returns {Promise<Object|null>} Memo history data with pagination or null if error
 */
export const getArchivedItemHistory = async (archivedItemId, options = {}) => {
  try {
    if (!archivedItemId) {
      throw new Error('archivedItemId is required');
    }

    const url = ENDPOINTS.GET_ARCHIVED_ITEM_HISTORY.replace(':archivedItemId', archivedItemId);

    const params = new URLSearchParams();

    if (options.page) {
      params.append('page', options.page.toString());
    }
    if (options.limit) {
      params.append('limit', options.limit.toString());
    }

    const { data } = await axios.get(`${url}?${params.toString()}`);

    if (data && data.success) {
      return data.data;
    }

    return null;
  } catch (error) {
    console.error('Failed to fetch archived item history:', error);
    throw error;
  }
};

export const getDepositVsRentReport = async (mastersheetId) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const url = ENDPOINTS.GET_DEPOSIT_VS_RENT_REPORT.replace(":mastersheetId", mastersheetId);
  const { data } = await axios.get(url);
  return data;
};

export const exportDepositVsRentExcel = async (mastersheetId) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const url = ENDPOINTS.EXPORT_DEPOSIT_VS_RENT_EXCEL.replace(":mastersheetId", mastersheetId);
  const response = await axios.get(url, { responseType: "blob" });
  return response.data;
};

export const getVacancyLeaseExpiryReport = async (mastersheetId) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const url = ENDPOINTS.GET_VACANCY_LEASE_EXPIRY_REPORT.replace(":mastersheetId", mastersheetId);
  const { data } = await axios.get(url);
  return data;
};

export const exportVacancyLeaseExpiryExcel = async (mastersheetId) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const url = ENDPOINTS.EXPORT_VACANCY_LEASE_EXPIRY_EXCEL.replace(":mastersheetId", mastersheetId);
  const response = await axios.get(url, { responseType: "blob" });
  return response.data;
};

export const getUtilityAccountsReport = async (mastersheetId) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const url = ENDPOINTS.GET_UTILITY_ACCOUNTS_REPORT.replace(":mastersheetId", mastersheetId);
  const { data } = await axios.get(url);
  return data;
};

export const exportUtilityAccountsExcel = async (mastersheetId) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const url = ENDPOINTS.EXPORT_UTILITY_ACCOUNTS_EXCEL.replace(":mastersheetId", mastersheetId);
  const response = await axios.get(url, { responseType: "blob" });
  return response.data;
};

export const getInsuranceReport = async (mastersheetId, days = 30) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const url = ENDPOINTS.GET_INSURANCE_REPORT.replace(":mastersheetId", mastersheetId);
  const { data } = await axios.get(url, { params: { days } });
  return data;
};

export const exportInsuranceReportExcel = async (mastersheetId, days = 30) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const url = ENDPOINTS.EXPORT_INSURANCE_REPORT_EXCEL.replace(":mastersheetId", mastersheetId);
  const response = await axios.get(url, { params: { days }, responseType: "blob" });
  return response.data;
};

export const getPadReport = async (mastersheetId, updatedWithinDays) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const url = ENDPOINTS.GET_PAD_REPORT.replace(":mastersheetId", mastersheetId);
  const params = updatedWithinDays ? { updatedWithinDays } : {};
  const { data } = await axios.get(url, { params });
  return data;
};

export const exportPadReportExcel = async (mastersheetId, updatedWithinDays) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const url = ENDPOINTS.EXPORT_PAD_REPORT_EXCEL.replace(":mastersheetId", mastersheetId);
  const params = updatedWithinDays ? { updatedWithinDays } : {};
  const response = await axios.get(url, { params, responseType: "blob" });
  return response.data;
};

export const getHistoricAsOf = async (mastersheetId, date) => {
  if (!mastersheetId || !date) throw new Error("mastersheetId and date are required");
  const url = ENDPOINTS.GET_HISTORIC_AS_OF.replace(":mastersheetId", mastersheetId);
  const { data } = await axios.get(url, { params: { date } });
  return data;
};

export const exportHistoricAsOfExcel = async (mastersheetId, date) => {
  if (!mastersheetId || !date) throw new Error("mastersheetId and date are required");
  const url = ENDPOINTS.EXPORT_HISTORIC_AS_OF_EXCEL.replace(":mastersheetId", mastersheetId);
  const response = await axios.get(url, { params: { date }, responseType: "blob" });
  return response.data;
};

export const getHistoricResidents = async (mastersheetId, from, to) => {
  if (!mastersheetId || !from || !to) throw new Error("mastersheetId, from and to are required");
  const url = ENDPOINTS.GET_HISTORIC_RESIDENTS.replace(":mastersheetId", mastersheetId);
  const { data } = await axios.get(url, { params: { from, to } });
  return data;
};

export const exportHistoricResidentsExcel = async (mastersheetId, from, to) => {
  if (!mastersheetId || !from || !to) throw new Error("mastersheetId, from and to are required");
  const url = ENDPOINTS.EXPORT_HISTORIC_RESIDENTS_EXCEL.replace(":mastersheetId", mastersheetId);
  const response = await axios.get(url, { params: { from, to }, responseType: "blob" });
  return response.data;
};

export const getHistoricAvailableMonths = async (mastersheetId) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const url = ENDPOINTS.GET_HISTORIC_AVAILABLE_MONTHS.replace(":mastersheetId", mastersheetId);
  const { data } = await axios.get(url);
  return data;
};

export const createHistoricSnapshot = async (mastersheetId) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const url = ENDPOINTS.CREATE_HISTORIC_SNAPSHOT.replace(":mastersheetId", mastersheetId);
  const { data } = await axios.post(url);
  return data;
};

export const getResidentDirectory = async ({ property_id, building_name }) => {
  if (!property_id) throw new Error("property_id is required");
  const { data } = await axios.get(ENDPOINTS.RESIDENT_DIRECTORY, {
    params: { property_id, ...(building_name ? { building_name } : {}) },
  });
  return data;
};

/**
 * Save an inline contact edit back onto the live mastersheet. Pass only the field
 * being changed — an omitted field is left as-is, an empty string clears it.
 */
export const saveResidentContact = async ({
  property_id,
  building_name,
  unit_no,
  name,
  email,
  phone,
}) => {
  const { data } = await axios.patch(ENDPOINTS.RESIDENT_DIRECTORY_CONTACT, {
    property_id,
    building_name,
    unit_no,
    name,
    ...(email !== undefined ? { email } : {}),
    ...(phone !== undefined ? { phone } : {}),
  });
  return data;
};

export const exportResidentDirectoryExcel = async ({
  property_id,
  building_name,
  property_name,
}) => {
  if (!property_id) throw new Error("property_id is required");
  const response = await axios.get(ENDPOINTS.EXPORT_RESIDENT_DIRECTORY_EXCEL, {
    params: {
      property_id,
      ...(building_name ? { building_name } : {}),
      ...(property_name ? { property_name } : {}),
    },
    responseType: "blob",
  });
  return response.data;
};

/** The import template, prefilled with the site's current roster. */
export const getResidentDirectoryTemplate = async ({
  property_id,
  building_name,
  property_name,
}) => {
  if (!property_id) throw new Error("property_id is required");
  const response = await axios.get(ENDPOINTS.RESIDENT_DIRECTORY_TEMPLATE, {
    params: {
      property_id,
      ...(building_name ? { building_name } : {}),
      ...(property_name ? { property_name } : {}),
    },
    responseType: "blob",
  });
  return response.data;
};

/** mode "preview" returns the row diff without writing; "apply" upserts. */
export const importResidentDirectoryRoster = async ({
  file,
  property_id,
  building_name,
  mode,
}) => {
  if (!property_id) throw new Error("property_id is required");
  const formData = new FormData();
  formData.append("file", file);
  formData.append("property_id", property_id);
  if (building_name) formData.append("building_name", building_name);
  formData.append("mode", mode);
  const { data } = await axios.post(ENDPOINTS.RESIDENT_DIRECTORY_IMPORT, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: mode === "apply" ? 0 : 60000,
  });
  return data;
};

/** Resident renames on a unit. */
export const saveResidentUnit = async ({ property_id, building_name, unit_no, name_edit }) => {
  const { data } = await axios.patch(ENDPOINTS.RESIDENT_DIRECTORY_UNIT, {
    property_id,
    building_name,
    unit_no,
    ...(name_edit !== undefined ? { name_edit } : {}),
  });
  return data;
};
